import axios from "../../utils/axios";
import { buildQueryStringList } from "../../utils/stringUtils";
import type { PagedResponse } from "../types/api";
import type { AuditDTO } from "../types/auditDtos";

const apiUrl = process.env.REACT_APP_HS_API_URL;

const getBySiteExternalId = ({ siteExternalId, pageNum, pageSize }) =>
    axios.get<PagedResponse<AuditDTO>>(`${apiUrl}/audit/forsite/${siteExternalId}?pageSize=${pageSize}&pageNum=${pageNum}`);

const fetchAuditsForSite = (externalIds) => {

    const queryParams = new URLSearchParams();

    // Add site external IDs
    externalIds.forEach(id => queryParams.append('siteExternalIds', id));

    return axios.get<AuditDTO[]>(`${apiUrl}/audit/site?${queryParams.toString()}`);
};

const downloadPdfByAuditId = (auditId, isDriAudit) =>
    axios.get<ArrayBuffer>(`${apiUrl}/audit/download/${auditId}?isDriAudit=${isDriAudit}`, {
        responseType: "arraybuffer",
    });

// Returns a single `AuditQueryType` value; AuditQueryType is [StringEnumConverter] so it serializes as a string.
const fetchLatestAuditTypeForSites = (externalIds) =>
    axios.get<string>(
        `${apiUrl}/audit/sitelatesttype/${buildQueryStringList(
            "siteExternalIds",
            externalIds
        )}`
    );

const replacePdfForAudit = ({ auditId, file }) => {
    const formData = new FormData();
    formData.append("file", file);

    return axios.post<void>(
        `${apiUrl}/audit/replacepdf/${auditId}`,
        formData,
        {
            headers: {
                "Content-Type": "multipart/form-data",
            },
        }
    );
};

const uploadAuditActionFile = async (
    formData,
    siteExternalId,
    auditId
) => {
    try {
        const response = await axios.post<void>(
            `${apiUrl}/audit/uploadAuditActionFile/${siteExternalId}/${auditId}`,
            formData,
            {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            }
        );
        return response;
    } catch (error) {
        throw error;
    }
};

// Deferred: AuditInsights builds a flattened CSV/HTML export shape (per-rating
// columns) from this data via tangled local mapping; typing it surfaced that the
// export rows don't carry the rating keys the HTML reads. Left `any` pending an
// export-logic review (AuditInsightsDTO is defined in auditDtos.ts for when it's done).
const fetchAuditInsights = (externalIds, auditType) => axios.get(`${apiUrl}/audit/insights/${auditType}${buildQueryStringList("siteExternalIds", externalIds)}`);

const recallAudit = (auditId) => axios.delete<void>(`${apiUrl}/audit/recall/${auditId}`);

const auditService = {
    fetchAuditsForSite,
    downloadPdfByAuditId,
    fetchLatestAuditTypeForSites,
    getBySiteExternalId,
    replacePdfForAudit,
    uploadAuditActionFile,
    fetchAuditInsights,
    recallAudit,
};

export default auditService;
